import React from 'react'
import { useNavigate } from 'react-router-dom'
import { LockOutlined } from '@ant-design/icons'
import { authApi } from 'api/auth'
import { appActions } from 'store/app'
import { superAdminRoutes } from 'shared/constants/routes'
import { SCREEN_LOCK_PIN_KEY, SCREEN_LOCK_STATE_KEY } from 'shared/constants/storage'
import { useAppDispatch } from 'shared/hooks/useReduxHooks'
import { showServerError } from 'shared/utils/showServerError'
import logo from 'assets/icons/logo.svg'
import styles from './ScreenLock.module.scss'

const PIN_LENGTH = 4
const KEYPAD_DIGITS = ['1', '2', '3', '4', '5', '6', '7', '8', '9']

const STEP_TEXTS = {
  create: {
    title: 'Задайте пин-код',
    subtitle: 'Он понадобится, чтобы снять блокировку'
  },
  repeat: {
    title: 'Повторите пин-код',
    subtitle: 'Введите те же 4 цифры ещё раз'
  },
  unlock: {
    title: 'Экран заблокирован',
    subtitle: 'Введите пин-код, чтобы продолжить работу'
  }
}

export const ScreenLock: React.FC = () => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()

  const [pin, setPin] = React.useState(() => localStorage.getItem(SCREEN_LOCK_PIN_KEY))
  // Блокировка без пин-кода снималась бы только выходом из аккаунта, поэтому её игнорируем
  const [isLocked, setIsLocked] = React.useState(
    () => localStorage.getItem(SCREEN_LOCK_STATE_KEY) === '1' && Boolean(localStorage.getItem(SCREEN_LOCK_PIN_KEY))
  )
  // Пин-код ещё не задан — перед первой блокировкой просим придумать его
  const [isCreatingPin, setIsCreatingPin] = React.useState(false)
  const [firstEnteredPin, setFirstEnteredPin] = React.useState('')
  const [value, setValue] = React.useState('')
  const [error, setError] = React.useState('')
  const overlayRef = React.useRef<HTMLDivElement>(null)

  const isVisible = isLocked || isCreatingPin
  const step = isLocked ? 'unlock' : firstEnteredPin ? 'repeat' : 'create'

  const lock = () => {
    localStorage.setItem(SCREEN_LOCK_STATE_KEY, '1')
    setIsLocked(true)
    setIsCreatingPin(false)
    setFirstEnteredPin('')
    setValue('')
    setError('')
  }

  const handleLockClick = () => {
    setValue('')
    setError('')

    if (pin) return lock()

    setFirstEnteredPin('')
    setIsCreatingPin(true)
  }

  const handleCancelCreate = () => {
    setIsCreatingPin(false)
    setFirstEnteredPin('')
    setValue('')
    setError('')
  }

  const handleComplete = (enteredPin: string) => {
    if (step === 'unlock') {
      if (enteredPin !== pin) {
        setValue('')
        setError('Неверный пин-код')
        return
      }

      localStorage.removeItem(SCREEN_LOCK_STATE_KEY)
      setIsLocked(false)
      setValue('')
      setError('')
      // С терминала работают только с ПолоКлубом, поэтому после разблокировки
      // возвращаем на его страницу, а не туда, где экран заблокировали
      navigate(superAdminRoutes.POLO_CLUB)
      return
    }

    if (step === 'create') {
      setFirstEnteredPin(enteredPin)
      setValue('')
      return
    }

    if (enteredPin !== firstEnteredPin) {
      setFirstEnteredPin('')
      setValue('')
      setError('Пин-коды не совпали, задайте заново')
      return
    }

    localStorage.setItem(SCREEN_LOCK_PIN_KEY, enteredPin)
    setPin(enteredPin)
    lock()
  }

  const handleDigit = (digit: string) => {
    if (value.length >= PIN_LENGTH) return

    const nextValue = value + digit
    setValue(nextValue)
    setError('')

    if (nextValue.length === PIN_LENGTH) handleComplete(nextValue)
  }

  const handleBackspace = () => {
    setValue((current) => current.slice(0, -1))
    setError('')
  }

  // Пин-код забыт — выход из аккаунта остаётся единственным способом снять блокировку,
  // поэтому вместе с ней сбрасываем и сам пин-код
  const handleLogout = async () => {
    try {
      await authApi.logout()
    } catch (e) {
      showServerError(e)
    } finally {
      localStorage.removeItem(SCREEN_LOCK_PIN_KEY)
      localStorage.removeItem(SCREEN_LOCK_STATE_KEY)
      dispatch(appActions.logout())
    }
  }

  // Фокус не должен уходить под оверлей: иначе с клавиатуры можно нажать кнопку
  // в заблокированной админке
  React.useEffect(() => {
    if (!isVisible) return undefined

    const holdFocus = (event: FocusEvent) => {
      if (!overlayRef.current?.contains(event.target as Node)) overlayRef.current?.focus()
    }

    overlayRef.current?.focus()
    document.body.style.overflow = 'hidden'
    document.addEventListener('focusin', holdFocus)

    return () => {
      document.body.style.overflow = ''
      document.removeEventListener('focusin', holdFocus)
    }
  }, [isVisible])

  React.useEffect(() => {
    if (!isVisible) return undefined

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Backspace') return handleBackspace()
      if (/^\d$/.test(event.key)) handleDigit(event.key)
    }

    window.addEventListener('keydown', handleKeyDown)

    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [isVisible, step, value, pin, firstEnteredPin])

  if (!isVisible) {
    return (
      <button
        type='button'
        className={styles.button}
        onClick={handleLockClick}
        aria-label='Заблокировать экран'
      >
        <LockOutlined />
      </button>
    )
  }

  return (
    <div className={styles.overlay} ref={overlayRef} tabIndex={-1}>
      <img src={logo} alt='' className={styles.logo} />
      <div className={styles.title}>{STEP_TEXTS[step].title}</div>
      <div className={styles.subtitle}>{STEP_TEXTS[step].subtitle}</div>
      <div className={styles.dots}>
        {Array.from({ length: PIN_LENGTH }).map((_, index) => (
          <span
            key={index}
            className={`${styles.dot} ${index < value.length ? styles.dotFilled : ''}`}
          />
        ))}
      </div>
      <div className={styles.error}>{error}</div>
      <div className={styles.keypad}>
        {KEYPAD_DIGITS.map((digit) => (
          <button
            key={digit}
            type='button'
            className={styles.key}
            onClick={() => handleDigit(digit)}
          >
            {digit}
          </button>
        ))}
        {isLocked ? (
          <span />
        ) : (
          <button
            type='button'
            className={`${styles.key} ${styles.keyText}`}
            onClick={handleCancelCreate}
          >
            Отмена
          </button>
        )}
        <button type='button' className={styles.key} onClick={() => handleDigit('0')}>
          0
        </button>
        <button
          type='button'
          className={`${styles.key} ${styles.keyText}`}
          onClick={handleBackspace}
          aria-label='Стереть цифру'
        >
          Стереть
        </button>
      </div>
      {isLocked && (
        <button type='button' className={styles.logout} onClick={handleLogout}>
          Забыли пин-код? Выйти из аккаунта
        </button>
      )}
    </div>
  )
}
